import React, { FC, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import useGetUserInformation from 'src/store/hooks/useGetUserInformation';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Box from '@amzn/meridian/box';
import { FloorplanProvider } from '@amzn/ihm-floorplan-viewer';
import getMidwayRedirectRoutes from 'src/utils/midwayRedirectRoutesHelper';
import { CONTACT_EMAIL } from 'src/constants/strings';
import AgileHome from '@components/agile/AgileHome';
import AgileManagement from '@components/agile/AgileManagement';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import AssortmentSearch from '@components/agile/AssortmentSearch';
import ItemAssortmentManagement from '@components/agile/ItemAssortmentManagement';
import ProductReadiness from '@components/productReadiness/ProductReadiness';
import WelcomePage from '@components/agile/welcomePage';
import ProductGroupsPage from 'src/pages/ProductGroupsPage';
import ZoneViewer from './agile/ZoneViewer';
import { SwitchButtonValue } from './agile/types/zoneMapType';
import AgileContext from './agile/context/AgileContext';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import AgileManagementV2 from '@components/agile/AgileManagementV2';
import { programRegistry } from 'src/config/programRegistry';
import { ProgramConfig } from 'src/types/programConfig';

const Component: FC = () => {
  const urlParams: AgileURLParameterTypes = {
    storeId: (NavigationHelper.getQueryParam('storeId') as string) ?? '',
    date: (NavigationHelper.getQueryParam('date') as string) ?? '',
    zoneId: (NavigationHelper.getQueryParam('zoneId') as string) ?? '',
    subcategoryId: (NavigationHelper.getQueryParam('subcategoryId') as string) || '',
    demo: NavigationHelper.getQueryParam('demo') ?? undefined,
  };

  const [urlParameter, setUrlParameter] = useState<AgileURLParameterTypes>(urlParams);
  const [switchButtonValue, setSwitchButtonValue] = useState<SwitchButtonValue | undefined>(
    SwitchButtonValue.ViewZoneMap,
  );
  const [enableZoneListInHeader, setEnableZoneListInHeader] = useState(true);

  const { isError, isLoading } = useGetUserInformation();

  const agileContextValue = useMemo(
    () => ({
      enableZoneListInHeader,
      setEnableZoneListInHeader,
    }),
    [enableZoneListInHeader],
  );
  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);

  const registryEntries = Object.values(programRegistry);
  const firstRoutePrefix = registryEntries[0].routePrefix;

  // Routes are conditionally rendered based on feature toggles.
  // When a feature is disabled, the route is not mounted — navigating to it
  // falls through to the banner's WelcomePage index route.

  const renderAssortmentComponent = (config: ProgramConfig) => {
    if (config.features.itemAssortment?.mode === 'scancode') {
      return <ItemAssortmentManagement urlParams={urlParameter} setUrlParameter={setUrlParameter} />;
    }
    return nextGenEnabled ? (
      <AgileManagementV2 urlParams={urlParameter} setUrlParameter={setUrlParameter} />
    ) : (
      <AgileManagement urlParams={urlParameter} setUrlParameter={setUrlParameter} />
    );
  };

  if (isLoading) {
    return (
      <Box>
        <Loader data-cy="user_info_loading" />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box data-cy="user_info_loading_failed">
        Failed to load user information. Please log into&nbsp;
        <Link href={getMidwayRedirectRoutes(('alpha' as string).toLowerCase())}>Midway.</Link>
        If you continue to see this message, please reach out to&nbsp;
        <Link href={`mailto: ${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link> to grant you permission for access.
      </Box>
    );
  }
  return (
    <div>
      <AgileContext.Provider value={agileContextValue}>
        <Routes>
          {registryEntries.map((config) => (
            <Route key={config.routePrefix} path={config.routePrefix} element={<AgileHome />}>
              {config.features.zoneMap && (
                <Route
                  path="zoneCreation"
                  element={
                    <FloorplanProvider>
                      <ZoneViewer
                        urlParams={urlParams}
                        setUrlParameter={setUrlParameter}
                        switchButtonValue={switchButtonValue}
                        setSwitchButtonValue={setSwitchButtonValue}
                      />
                    </FloorplanProvider>
                  }
                />
              )}
              {config.features.itemAssortment && (
                <Route path="agileManagement" element={renderAssortmentComponent(config)} />
              )}
              {config.features.productReadiness && (
                <Route
                  path="productReadiness"
                  element={<ProductReadiness urlParams={urlParams} setUrlParameter={setUrlParameter} />}
                />
              )}
              {config.features.assortmentSearch && (
                <Route
                  path="assortmentSearch"
                  element={<AssortmentSearch urlParams={urlParameter} setUrlParameter={setUrlParameter} />}
                />
              )}
              <Route path="productGroups" element={<ProductGroupsPage />} />
              <Route path="" element={<WelcomePage />} />
            </Route>
          ))}
          <Route path="/*" element={<Navigate to={firstRoutePrefix} />} />
        </Routes>
      </AgileContext.Provider>
    </div>
  );
};

export default Component;
